/**
 * One shout out, compact (Sasha 10/02, so the panel fits more than three):
 * line 1 who it is to and from, line 2 its title, last line the value tag,
 * hearts and comments. The message shows in a popover on hover, on keyboard
 * Enter, and on tap (touch has no hover), so reading one never pushes the
 * cards below around. Opening the comments shows the message inline above the
 * thread; the parent keeps only one thread open at a time.
 *
 * A shout out given before titles existed falls back to its message as the
 * title line (none should remain after the 10/02 backfill and migration 008).
 *
 * Hearts are optimistic: the count moves on click and the server call follows.
 */

import { useEffect, useState, type KeyboardEvent } from 'react';
import {
  Button,
  Input,
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  ChatRegular,
  DeleteRegular,
  HeartFilled,
  HeartRegular,
  PeopleRegular,
} from '@fluentui/react-icons';
import { borderRadius, semanticColors, veracityColors } from '@shared/ui';
import { useAddComment, useComments, useDeleteComment, useSetHeart, type ShoutOut } from './api';
import { Confetti, Initials, recipientsLabel, relativeTime } from './bits';

const useStyles = makeStyles({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.editorial,
    border: `1px solid ${semanticColors.borderSubtle}`,
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    transitionProperty: 'border-color',
    transitionDuration: '160ms',
    ':hover': { border: `1px solid ${semanticColors.borderDefault}` },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  who: {
    flex: 1,
    minWidth: 0,
    fontSize: '12.5px',
    color: semanticColors.textSecondary,
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  to: {
    fontWeight: 600,
    color: veracityColors.midnightCharcoal,
  },
  when: {
    fontSize: '11.5px',
    color: semanticColors.textSecondary,
    flexShrink: 0,
  },
  // The title: a button (keyboard-reachable, tap target) that opens the
  // message popover, and reads as a headline.
  title: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
    color: veracityColors.midnightCharcoal,
    overflowWrap: 'anywhere',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '2px' },
  },
  message: {
    fontSize: '13.5px',
    lineHeight: 1.5,
    color: '#3A3431',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  popover: {
    maxWidth: '340px',
    padding: '12px 14px',
    borderRadius: borderRadius.large,
  },
  foot: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tag: {
    marginRight: 'auto',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: veracityColors.midnightCharcoal,
    backgroundColor: veracityColors.gentleHaze,
    borderRadius: borderRadius.circular,
    padding: '3px 9px',
  },
  pill: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: borderRadius.circular,
    border: `1px solid ${semanticColors.borderSubtle}`,
    backgroundColor: '#FFFFFF',
    color: semanticColors.textSecondary,
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: '160ms',
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '2px' },
  },
  hearted: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: '#FFFFFF',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  pillIcon: { fontSize: '14px' },

  thread: {
    borderTop: `1px solid ${semanticColors.borderSubtle}`,
    paddingTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    animationName: {
      from: { opacity: 0, transform: 'translateY(-4px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: '200ms',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
  comment: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  commentText: {
    flex: 1,
    minWidth: 0,
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: '#3A3431',
    overflowWrap: 'anywhere',
  },
  commentMeta: {
    fontSize: '11.5px',
    color: semanticColors.textSecondary,
  },
  empty: {
    fontSize: '12.5px',
    color: semanticColors.textSecondary,
  },
  compose: {
    display: 'flex',
    gap: '6px',
  },
  error: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface ShoutOutCardProps {
  shoutOut: ShoutOut;
  open: boolean;
  onToggle: () => void;
  /** Shout Out Admin: shows a delete control on each comment. */
  canModerate: boolean;
  /** The full feed (/central/shout-outs): title and whole message always shown, no popover. */
  showFull?: boolean;
  /** Central admins on the feed: opens the "who engaged" column for this card. */
  onEngagement?: () => void;
  engagementOpen?: boolean;
}

export function ShoutOutCard({ shoutOut: s, open, onToggle, canModerate, showFull = false, onEngagement, engagementOpen = false }: ShoutOutCardProps) {
  const styles = useStyles();
  const setHeart = useSetHeart();
  const comments = useComments(s.id, open);
  const addComment = useAddComment();
  const deleteComment = useDeleteComment();

  // Optimistic heart: local state wins until the refetch lands.
  const [heart, setHeartLocal] = useState<{ on: boolean; count: number } | null>(null);
  const hearted = heart?.on ?? s.heartedByMe;
  const heartCount = heart?.count ?? s.heartCount;
  // Drop the optimistic value once the server's numbers arrive.
  useEffect(() => setHeartLocal(null), [s.heartCount, s.heartedByMe]);
  const [burst, setBurst] = useState(0);
  const [draft, setDraft] = useState('');

  const toggleHeart = () => {
    const on = !hearted;
    setHeartLocal({ on, count: Math.max(0, heartCount + (on ? 1 : -1)) });
    if (on) setBurst((b) => b + 1);
    setHeart.mutate({ id: s.id, on }, { onError: () => setHeartLocal(null) });
  };

  const submit = () => {
    const body = draft.trim();
    if (!body || addComment.isPending) return;
    addComment.mutate({ id: s.id, body }, { onSuccess: () => setDraft('') });
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const recipients = recipientsLabel(s.recipients);
  const firstRecipient = s.recipients[0]?.name ?? recipients;
  const [peek, setPeek] = useState(false);

  return (
    <article className={styles.card}>
      <div className={styles.head}>
        <Initials name={firstRecipient} size={26} />
        <span className={styles.who} title={`${recipients} · from ${s.sender.name}`}>
          <span className={styles.to}>{recipients}</span> · from {s.sender.name}
        </span>
        <span className={styles.when}>{relativeTime(s.publishedAt ?? s.createdAt)}</span>
      </div>
      {open || showFull ? (
        s.title && <span className={styles.title}>{s.title}</span>
      ) : (
        <Popover
          openOnHover
          mouseLeaveDelay={150}
          withArrow
          positioning="below-start"
          open={peek}
          onOpenChange={(_, d) => setPeek(d.open)}
        >
          <PopoverTrigger disableButtonEnhancement>
            <button type="button" className={styles.title} aria-label={`${s.title ?? 'Shout out'}: read the message`}>
              {s.title ?? s.message}
            </button>
          </PopoverTrigger>
          <PopoverSurface className={styles.popover}>
            <div className={styles.message}>{s.message}</div>
          </PopoverSurface>
        </Popover>
      )}
      {(open || showFull) && <div className={styles.message}>{s.message}</div>}
      <div className={styles.foot}>
        <span className={styles.tag}>{s.valueLabel}</span>
        <button
          type="button"
          className={mergeClasses(styles.pill, hearted && styles.hearted)}
          onClick={toggleHeart}
          aria-pressed={hearted}
          aria-label={hearted ? `Remove your heart (${heartCount})` : `Heart this (${heartCount})`}
        >
          {hearted ? <HeartFilled className={styles.pillIcon} /> : <HeartRegular className={styles.pillIcon} />}
          {heartCount}
          <Confetti burstKey={burst} />
        </button>
        <button
          type="button"
          className={styles.pill}
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${s.commentCount} comments`}
        >
          <ChatRegular className={styles.pillIcon} />
          {s.commentCount}
        </button>
        {onEngagement && (
          <button
            type="button"
            className={mergeClasses(styles.pill, engagementOpen && styles.hearted)}
            onClick={onEngagement}
            aria-pressed={engagementOpen}
            aria-label="Who engaged with this shout out"
            title="Who engaged (admins)"
          >
            <PeopleRegular className={styles.pillIcon} />
            Who
          </button>
        )}
      </div>

      {open && (
        <div className={styles.thread}>
          {comments.isLoading && <Spinner size="tiny" />}
          {comments.data?.length === 0 && <span className={styles.empty}>No comments yet. Be the first.</span>}
          {comments.data?.map((c) => (
            <div key={c.id} className={styles.comment}>
              <Initials name={c.author.name} size={24} />
              <div className={styles.commentText}>
                <div className={styles.commentMeta}>
                  {c.author.name} · {relativeTime(c.createdAt)}
                </div>
                {c.body}
              </div>
              {canModerate && (
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  aria-label={`Delete comment from ${c.author.name}`}
                  disabled={deleteComment.isPending}
                  onClick={() => deleteComment.mutate(c.id)}
                />
              )}
            </div>
          ))}
          <div className={styles.compose}>
            <Input
              size="small"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Add a comment…"
              value={draft}
              maxLength={500}
              onChange={(_, d) => setDraft(d.value)}
              onKeyDown={onKey}
              aria-label="Add a comment"
            />
            <Button size="small" appearance="primary" onClick={submit} disabled={!draft.trim() || addComment.isPending}>
              Post
            </Button>
          </div>
          {addComment.error && <span className={styles.error}>{addComment.error.message}</span>}
        </div>
      )}
    </article>
  );
}
